import React, { useEffect, useRef, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Badge,
  Box,
  Button,
  Circle,
  Flex,
  HStack,
  Icon,
  Image,
  Progress,
  Text,
  VStack,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import {
  FaCalendarAlt,
  FaCheck,
  FaClipboardList,
  FaImage,
  FaMapMarkerAlt,
  FaMusic,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import ActuacionDatosFormFields from './ActuacionDatosFormFields.jsx';
import { toast } from '@/components/ui/toaster';
import { actuacionesAPI } from '../services/api';
import { buildActuacionDatosPayload } from '../utils/actuacionPayload.js';
import { formatActuacionHorario, labelTipoActuacion } from '../constants/tipoActuacion.js';
import { useTiposFormacionCatalogo } from '../hooks/useTiposFormacionCatalogo.js';

const STEPS = [
  { n: 1, label: 'Datos básicos', short: 'Básicos', icon: FaMusic },
  { n: 2, label: 'Fecha y lugar', short: 'Lugar', icon: FaMapMarkerAlt },
  { n: 3, label: 'Detalles', short: 'Detalles', icon: FaClipboardList },
  { n: 4, label: 'Cartel', short: 'Cartel', icon: FaImage },
];

function startOfLocalDayFromYMD(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd).trim())) return null;
  const [y, mo, da] = String(ymd).trim().split('-').map(Number);
  const x = new Date(y, mo - 1, da);
  x.setHours(0, 0, 0, 0);
  const t = x.getTime();
  return Number.isNaN(t) ? null : t;
}

function localDateYMD(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function ActuacionCreateWizard({
  isOpen,
  onClose,
  form,
  setForm,
  plantillas,
  ubicLlegadaDistinta,
  setUbicLlegadaDistinta,
  createFormKey,
  fechaMinYMD = localDateYMD(),
  toast,
  onCreated,
}) {
  const { tipos: tiposFormacionCatalogo } = useTiposFormacionCatalogo();
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [cartelFile, setCartelFile] = useState(null);
  const [cartelPreviewUrl, setCartelPreviewUrl] = useState('');
  const cartelInputRef = useRef(null);

  const stepBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const stepActiveBg = useColorModeValue('purple.600', 'purple.400');
  const stepMuted = useColorModeValue('gray.400', 'gray.500');
  const dropBg = useColorModeValue('gray.50', 'gray.700');
  const dropBorder = useColorModeValue('purple.200', 'purple.500');
  const summaryBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const summaryBorder = useColorModeValue('purple.100', 'whiteAlpha.200');

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setCartelFile(null);
    setCartelPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, [isOpen, createFormKey]);

  useEffect(() => () => {
    if (cartelPreviewUrl) URL.revokeObjectURL(cartelPreviewUrl);
  }, [cartelPreviewUrl]);

  const validateStep = (s) => {
    if (s === 1) {
      if (!form.nombre?.trim()) {
        toast({ title: 'El nombre es obligatorio', status: 'warning' });
        return false;
      }
      return true;
    }
    if (s === 2) {
      const fechaYmd = form.fecha?.trim();
      if (fechaYmd) {
        const hoy0 = startOfLocalDayFromYMD(fechaMinYMD);
        const elegida0 = startOfLocalDayFromYMD(fechaYmd);
        if (hoy0 != null && elegida0 != null && elegida0 < hoy0) {
          toast({ title: 'La fecha no puede ser anterior a hoy', status: 'warning' });
          return false;
        }
      }
      return true;
    }
    if (s === 3) {
      if (form.necesidadAutobus) {
        const busCoordsOk =
          form.autobusLat != null &&
          form.autobusLng != null &&
          !Number.isNaN(Number(form.autobusLat)) &&
          !Number.isNaN(Number(form.autobusLng));
        if (!form.autobusUbicacion?.trim() || !form.autobusHoraSalida?.trim() || !busCoordsOk) {
          toast({
            title: 'Datos del autobús incompletos',
            description:
              'Busca la recogida del autobús, elige una sugerencia del listado (para fijar el punto en el mapa), indica la hora de salida, o desactiva «Preguntar por autobús».',
            status: 'warning',
            duration: 6000,
          });
          return false;
        }
      }
      return true;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(4, s + 1));
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handleCartelPick = (e) => {
    const file = e.target.files?.[0];
    if (cartelInputRef.current) cartelInputRef.current.value = '';
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Formato no válido', description: 'Usa JPEG, PNG o WebP.', status: 'warning' });
      return;
    }
    setCartelFile(file);
    setCartelPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const clearCartel = () => {
    setCartelFile(null);
    setCartelPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const handleCreate = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) return;
    try {
      setCreating(true);
      const res = await actuacionesAPI.create(
        buildActuacionDatosPayload({ ...form, nombre: form.nombre.trim() }, ubicLlegadaDistinta),
      );
      const newId = res.data?.id;
      if (newId && cartelFile) {
        try {
          await actuacionesAPI.uploadCartel(newId, cartelFile);
        } catch (err) {
          toast({
            title: 'Actuación creada, pero falló el cartel',
            description: err.response?.data?.message || 'Puedes subirlo desde el detalle.',
            status: 'warning',
            duration: 6000,
          });
          onCreated(newId);
          onClose();
          return;
        }
      }
      toast({
        title: cartelFile ? 'Actuación creada con cartel' : 'Actuación creada',
        status: 'success',
      });
      onCreated(newId);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message;
      const description = Array.isArray(msg) ? msg.join(', ') : msg || err.message;
      toast({ title: 'Error', description, status: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const plantillaNombre = plantillas.find((p) => p.id === form.plantillaId)?.nombre;

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" placement='center' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <Dialog.Positioner>
          <Dialog.Content maxH="92vh" rounded="2xl" overflow="hidden">
            <Dialog.Header
              pb={2}
              color="white"
              bg="purple.600"
              bgGradient="to-br"
              gradientFrom="purple.700"
              gradientTo="purple.500"
            >
              <HStack gap={3}>
                <Icon as={FaPlus} />
                <VStack align="start" gap={0}>
                  <Text>Nueva actuación</Text>
                  <Text fontSize="sm" fontWeight="normal" opacity={0.9}>
                    Paso {step} de {STEPS.length} · {STEPS[step - 1]?.label}
                  </Text>
                </VStack>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger color="white" />
            <Progress.Root value={(step / STEPS.length) * 100} size="xs" colorPalette="purple" w="full">
              <Progress.Track w="full">
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
            <Dialog.Body py={5}>
              <HStack gap={2} mb={6} justify="space-between" flexWrap="wrap">
                {STEPS.map((s, idx) => {
                  const done = step > s.n;
                  const active = step === s.n;
                  return (
                    <Flex key={s.n} align="center" flex={idx < STEPS.length - 1 ? 1 : 'none'} minW="72px">
                      <VStack gap={1}>
                        <Circle
                          size="36px"
                          bg={done ? 'green.500' : active ? stepActiveBg : stepBg}
                          color={done || active ? 'white' : stepMuted}
                          borderWidth={active ? '2px' : '0'}
                          borderColor="purple.300"
                        >
                          <Icon as={done ? FaCheck : s.icon} boxSize={4} />
                        </Circle>
                        <Text fontSize="2xs" fontWeight={active ? '700' : '500'} color={active ? 'purple.600' : stepMuted} textAlign="center">
                          {s.short}
                        </Text>
                      </VStack>
                      {idx < STEPS.length - 1 ? (
                        <Box flex={1} h="2px" bg={done ? 'green.300' : 'gray.200'} mx={1} minW="12px" />
                      ) : null}
                    </Flex>
                  );
                })}
              </HStack>

              {step <= 3 && isOpen ? (
                <ActuacionDatosFormFields
                  key={createFormKey}
                  form={form}
                  setForm={setForm}
                  plantillas={plantillas}
                  toast={toast}
                  enabled={isOpen}
                  ubicLlegadaDistinta={ubicLlegadaDistinta}
                  setUbicLlegadaDistinta={setUbicLlegadaDistinta}
                  searchResetKey={createFormKey}
                  fechaMinYMD={fechaMinYMD}
                  wizardStep={step}
                />
              ) : null}

              {step === 4 ? (
                <VStack align="stretch" gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Sube el cartel de la actuación (opcional). También podrás cambiarlo después desde el detalle.
                  </Text>
                  <input
                    ref={cartelInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleCartelPick}
                  />
                  <Box
                    rounded="xl"
                    borderWidth="2px"
                    borderStyle="dashed"
                    borderColor={cartelPreviewUrl ? 'purple.300' : dropBorder}
                    bg={dropBg}
                    p={4}
                    textAlign="center"
                    cursor="pointer"
                    onClick={() => cartelInputRef.current?.click()}
                    _hover={{ borderColor: 'purple.400', bg: 'purple.50' }}
                    transition="all 0.2s"
                  >
                    {cartelPreviewUrl ? (
                      <Box position="relative">
                        <Image
                          src={cartelPreviewUrl}
                          alt="Vista previa del cartel"
                          maxH="280px"
                          mx="auto"
                          rounded="lg"
                          objectFit="contain"
                        />
                        <Button
                          size="xs"
                          colorPalette="red"
                          variant="solid"
                          position="absolute"
                          top={2}
                          right={2}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearCartel();
                          }}><FaTrash />Quitar
                                              </Button>
                      </Box>
                    ) : (
                      <VStack py={8} gap={2}>
                        <Icon as={FaImage} boxSize={10} color="purple.400" />
                        <Text fontWeight="600" fontSize="sm">Haz clic para elegir imagen</Text>
                        <Text fontSize="xs" color="gray.500">JPEG, PNG o WebP · máx. 8 MB</Text>
                      </VStack>
                    )}
                  </Box>

                  <Box
                    rounded="xl"
                    borderWidth="1px"
                    borderColor={summaryBorder}
                    bg={summaryBg}
                    p={4}
                  >
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color="purple.600" mb={2}>
                      Resumen
                    </Text>
                    <Text fontWeight="700" fontSize="md" mb={2} lineClamp={2}>
                      {form.nombre || 'Sin nombre'}
                    </Text>
                    <HStack gap={2} flexWrap="wrap" mb={2}>
                      <Badge colorPalette="purple" variant="subtle">
                        {labelTipoActuacion(form.tipo, tiposFormacionCatalogo)}
                      </Badge>
                      {plantillaNombre ? (
                        <Badge colorPalette="gray" variant="subtle">{plantillaNombre}</Badge>
                      ) : null}
                      {cartelFile ? (
                        <Badge colorPalette="green" variant="subtle">Con cartel</Badge>
                      ) : null}
                    </HStack>
                    {form.fecha ? (
                      <HStack gap={2} fontSize="sm" color="gray.600">
                        <Icon as={FaCalendarAlt} color="purple.400" />
                        <Text>
                          {new Date(form.fecha).toLocaleDateString('es-ES')}
                          {formatActuacionHorario(form) ? ` · ${formatActuacionHorario(form)}` : ''}
                        </Text>
                      </HStack>
                    ) : null}
                    {form.ubicacion ? (
                      <HStack gap={2} fontSize="sm" color="gray.600" mt={1}>
                        <Icon as={FaMapMarkerAlt} color="gray.400" />
                        <Text lineClamp={2}>{form.ubicacion}</Text>
                      </HStack>
                    ) : null}
                  </Box>
                  <Text fontSize="xs" color="gray.500">
                    Paso 4/4 · Revisa el resumen y pulsa «Crear actuación».
                  </Text>
                </VStack>
              ) : null}

              {step < 4 ? (
                <Text fontSize="xs" color="gray.500" mt={4}>
                  Paso {step}/{STEPS.length} · {STEPS[step - 1]?.label}
                </Text>
              ) : null}
            </Dialog.Body>
            <Dialog.Footer borderTopWidth="1px">
              <HStack w="full" justify="space-between">
                <Button variant="ghost" onClick={step === 1 ? onClose : handleBack}>
                  {step === 1 ? 'Cancelar' : 'Atrás'}
                </Button>
                {step < 4 ? (
                  <Button colorPalette="purple" onClick={handleNext}>
                    Siguiente
                  </Button>
                ) : (
                  <Button
                    colorPalette="purple"
                    onClick={handleCreate}
                    loading={creating}
                    loadingText="Creando…"><FaPlus />Crear actuación
                                  </Button>
                )}
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
